import type { ReactNode } from 'react';

/**
 * Report page header (design prototype `.rtop`): the title, a mono meta line
 * ("Generated … · Version …"), and a row of tag pills — the health chip with
 * a status dot, then every connected source the report drew from. Actions
 * (the PDF download) sit top-right.
 *
 * Every meta piece is optional and simply drops out when the payload doesn't
 * carry it — older reports have no `_meta`.
 */

interface ReportHeaderProps {
  healthScore?: number | null;
  sources: string[];
  generatedAt?: string | null;
  version?: number | string | null;
  actions?: ReactNode;
}

function healthDot(score: number): string {
  if (score < 40) return 'var(--destructive)';
  if (score < 65) return 'var(--warning)';
  return 'var(--success)';
}

function formatDate(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function ReportHeader({
  healthScore,
  sources,
  generatedAt,
  version,
  actions,
}: ReportHeaderProps) {
  const metaParts: string[] = [];
  const date = generatedAt ? formatDate(generatedAt) : null;
  if (date) metaParts.push(`Generated ${date}`);
  if (version != null && version !== '') metaParts.push(`Version ${version}`);

  return (
    <div className="flex items-start justify-between gap-6 px-8 pt-6">
      <div className="min-w-0">
        <h1 className="text-[26px] font-bold leading-[1.15] tracking-[-0.02em] text-foreground">
          Business Analysis
        </h1>
        {metaParts.length > 0 && (
          <div className="mt-1.5 font-mono text-[12.5px] text-muted-foreground">
            {metaParts.join(' · ')}
          </div>
        )}
        {(healthScore != null || sources.length > 0) && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {healthScore != null && (
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-border bg-card px-2.5 py-[3px] text-[11px] font-medium text-foreground/80">
                <span
                  className="size-1.5 flex-none rounded-full"
                  style={{ background: healthDot(healthScore) }}
                />
                Health {healthScore} / 100
              </span>
            )}
            {sources.map((s) => (
              <span
                key={s}
                className="inline-flex items-center whitespace-nowrap rounded-full border border-border bg-card px-2.5 py-[3px] text-[11px] font-medium text-foreground/80"
              >
                {s}
              </span>
            ))}
          </div>
        )}
      </div>
      {actions && <div className="flex flex-none items-center gap-2">{actions}</div>}
    </div>
  );
}
